import React from "react";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import { MenuItem, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

/**
 * @function InputField
 * @description A MUI TextField wrapped in a div with a label and optional icon and select options.
 * @param {object} props Component props
 * @param {boolean} [props.required=false] Is the input required?
 * @param {string} [props.label] The label for the input
 * @param {string} [props.name] The name of the input
 * @param {string} [props.size="small"] The size of the input
 * @param {boolean} [props.disabled=false] Is the input disabled?
 * @param {string} [props.type="text"] The type of the input
 * @param {string} [props.value=""] The value of the input
 * @param {function} [props.onChange=() => {}] The function to call when the input changes
 * @param {import("react").ReactNode} [props.icon] An optional icon to display before the input
 * @param {boolean} [props.select=false] Is the input a select?
 * @param {{label: string, value: string}[]} [props.selectOptions=[]] The options for the select
 * @param {boolean} [props.error] Is there an error?
 * @param {string} [props.helperText] The helper text
 * @param {object} [props.style] Additional styles for the input
 * @param {boolean} [props.fullWidth] Should the input take full width?
 * @param {boolean} [props.showPassword=false] Should the password be shown?
 * @param {function} [props.togglePasswordVisibility=() => {}] Function to toggle password visibility
 * @param {string} [props.themeColor="default"] Theme color for the input
 * @param {boolean} [props.multiline=false] Is the input multiline?
 * @param {number} [props.minRows] Minimum rows for multiline input
 * @param {string} [props.placeholder=""] Placeholder text for the input
 * @param {import("react").Ref} [props.inputRef=null] Ref for the input element
 * @param {object} [props.inputSlotProps={}] Additional slot props for the input element
 * @param {string} [props.autoComplete="off"] AutoComplete attribute for the input
 * @returns {import("react").ReactElement} The component
 */
export const InputField = ({
  required,
  label,
  name,
  size = "small",
  disabled = false,
  type = "text",
  value = "",
  onChange = () => {},
  icon = null,
  select = false,
  selectOptions = [],
  selectOptionsMaxHeight = 300,
  error,
  helperText,
  style,
  fullWidth,
  showPassword = false,
  togglePasswordVisibility = () => {},
  themeColor = "default",
  multiline = false,
  minRows,
  placeholder = "",
  inputRef = null,
  inputSlotProps = {},
  autoComplete = "off",
}) => {
  const variantStyles =
    themeColor === "default"
      ? {}
      : {
          "& .MuiOutlinedInput-root": {
            color: themeColor,
            "& fieldset": { borderColor: themeColor },
            "&:hover fieldset": { borderColor: themeColor },
            "&.Mui-focused fieldset": { borderColor: themeColor },
          },
          "& .MuiInputLabel-root": { color: themeColor },
          "& .MuiInputLabel-root.Mui-focused": { color: themeColor },
          "& .MuiSvgIcon-root": { color: themeColor },
        };
  return (
    <TextField
      required={required}
      label={label}
      name={name}
      size={size}
      disabled={disabled}
      type={type === "password" ? (showPassword ? "text" : "password") : type}
      value={value}
      onChange={onChange}
      select={select}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      multiline={multiline}
      minRows={minRows}
      inputRef={inputRef}
      slotProps={{
        inputLabel: {
          shrink: type === "date" || type === "datetime-local" || type === "month" || undefined,
          sx: { "&.Mui-focused": { color: "#674BF5" } },
        },
        input: {
          ...inputSlotProps,
          startAdornment: icon ? <InputAdornment position="start">{icon}</InputAdornment> : undefined,
          endAdornment:
            type === "password" ? (
              <InputAdornment position="end">
                <IconButton onClick={togglePasswordVisibility} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ) : undefined,
        },
        htmlInput: inputSlotProps.htmlInput,
        formHelperText: {
          sx: {
            minHeight: "20px", // reserve space to prevent UI jump
            margin: 0, // remove default margin
          },
        },
        ...(select && {
          select: {
            MenuProps: {
              PaperProps: {
                sx: { maxHeight: selectOptionsMaxHeight },
              },
            },
          },
        }),
      }}
      autoComplete={autoComplete}
      sx={{
        mb: error && helperText ? 0 : 2,
        "& .MuiOutlinedInput-root": { "&.Mui-focused fieldset": { borderColor: "#674BF5" } },
        "& .MuiInputLabel-root.Mui-focused": { color: "#674BF5" },
        ...variantStyles,
        ...style,
      }}
      fullWidth={fullWidth}
    >
      {select && selectOptions.map((option) => <MenuItem key={option.value} value={option.value} children={option.label} />)}
    </TextField>
  );
};
